/**
 * Shipping quote
 * Behaviour for snippets/shipping-quote.liquid (design A, the delivery line).
 *
 * Prices come from Bespoke through Shopify, never from this file: each quote is a one-item
 * cart made through the tokenless Storefront API with the shopper's postcode, and Shopify
 * returns that cart's delivery options the way checkout gets them. The shopper's own cart
 * is never touched. The snippet's header explains the rest.
 *
 * Listens to the section's quantity input ('change', fired by the plus and minus buttons in
 * quantity-input.js) and the theme's own 'on:variant:change' event (documented in custom.js).
 */

if (!customElements.get('shipping-quote')) {
  const CREATE = `mutation QuoteCart($input: CartInput!) {
    cartCreate(input: $input) {
      cart { id lines(first: 1) { nodes { quantity } } }
      userErrors { message code }
    }
  }`;
  const RATES = `query QuoteRates($id: ID!) {
    cart(id: $id) {
      id
      ... @defer {
        deliveryGroups(first: 5, withCarrierRates: true) {
          nodes { deliveryOptions { code title estimatedCost { amount } } }
        }
      }
    }
  }`;

  const SAVED_KEY = 'cz-ship-to'; // localStorage: the shopper's postcode, suburb and state
  const CACHE_KEY = 'cz-sq:'; // sessionStorage: quotes by variant, quantity, postcode, suburb
  const CACHE_MS = 30 * 60 * 1000;
  const TIMEOUT_MS = 15000;
  // Declared crawlers never quote on arrival. Browsing agents are not listed: anything that
  // types a postcode is quoted like a shopper.
  const CRAWLER = /googlebot|bingbot|yandex|baiduspider|duckduckbot|applebot|gptbot|claudebot|perplexitybot|ahrefsbot|semrushbot|petalbot|bytespider|facebookexternalhit|crawler|spider|lighthouse|pagespeed/i;

  const ICON = {
    truck: '<svg class="cz-sq__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.5 6.5h11v9h-11zM13.5 9.5h4l3 3v3h-7z"/><circle cx="6.5" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/></svg>',
    target: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="6.5"/><circle cx="12" cy="12" r="2"/><path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3"/></svg>'
  };

  /** State from postcode ranges; used until the suburb data says otherwise. */
  const stateOf = (pc) => {
    if (!/^\d{4}$/.test(pc || '')) return null;
    const n = parseInt(pc, 10);
    if ((n >= 200 && n <= 299) || (n >= 2600 && n <= 2618) || (n >= 2900 && n <= 2920)) return 'ACT';
    if ((n >= 1000 && n <= 2599) || (n >= 2619 && n <= 2899) || (n >= 2921 && n <= 2999)) return 'NSW';
    if ((n >= 3000 && n <= 3999) || (n >= 8000 && n <= 8999)) return 'VIC';
    if ((n >= 4000 && n <= 4999) || (n >= 9000 && n <= 9999)) return 'QLD';
    if (n >= 5000 && n <= 5999) return 'SA';
    if (n >= 6000 && n <= 6999) return 'WA';
    if (n >= 7000 && n <= 7999) return 'TAS';
    if (n >= 800 && n <= 999) return 'NT';
    return null;
  };

  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const money = (n) => (n === 0 ? 'Free' : `$${n.toFixed(2)}`);
  const items = (q) => (q === 1 ? '1 item' : `${q} items`);
  const read = (store, key) => { try { return JSON.parse(store.getItem(key)); } catch (e) { return null; } };
  const write = (store, key, value) => { try { store.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ } };

  /** Checkout's rate titles are carrier names in capitals; shoppers get plain names. */
  const rateName = (option) => {
    const title = option.title || '';
    const t = title.toUpperCase();
    if (t.includes('PARCEL POST')) return 'Standard';
    if (t.includes('EXPRESS POST')) return 'Express';
    if (t.startsWith('WAREHOUSE PICKUP')) return 'Pick up from Darra';
    if (t.includes('LARGE FREIGHT')) return /premium/i.test(option.code || '') ? 'Large freight, premium' : 'Large freight';
    const plain = title.replace(/\[.*?\]/g, '').trim().toLowerCase();
    return plain.charAt(0).toUpperCase() + plain.slice(1);
  };

  const jsonCache = {};
  const loadJson = (url) => {
    if (!jsonCache[url]) {
      jsonCache[url] = fetch(url)
        .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
        .then((d) => d.p)
        .catch((e) => { delete jsonCache[url]; throw e; });
    }
    return jsonCache[url];
  };

  class ShippingQuote extends HTMLElement {
    connectedCallback() {
      this.cfg = JSON.parse(this.querySelector('[data-sq-config]').textContent);
      this.body = this.querySelector('[data-sq-body]');
      this.variantId = this.cfg.variantId;
      this.variantAvailable = this.cfg.variantAvailable !== false;
      this.qtyInput = document.getElementById(this.cfg.qtyInput);
      this.canLocate = 'geolocation' in navigator && window.isSecureContext;
      this.idBase = `cz-sq-${this.dataset.section}`;
      this.seq = 0;
      this.state = { mode: 'ask', pc: '', st: '', suburb: '', fromLoc: false, err: '', rates: [], suburbs: [] };

      const scope = this.closest('.product-info') || this.closest('.product');
      const picker = scope ? scope.querySelector('variant-picker') : null;
      if (picker) picker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      if (this.qtyInput) this.qtyInput.addEventListener('change', () => this.requote(600));

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('submit', this.handleSubmit.bind(this));
      this.addEventListener('input', this.handleInput.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));

      const saved = read(localStorage, SAVED_KEY);
      if (saved && stateOf(saved.pc)) {
        Object.assign(this.state, { pc: saved.pc, st: saved.st || stateOf(saved.pc), suburb: saved.suburb || '', mode: 'ready' });
        const arrive = () => {
          if (CRAWLER.test(navigator.userAgent)) this.render();
          else this.whenViewed(() => { if (this.state.mode === 'ready') this.requote(0); });
        };
        if (saved.v === 1) {
          arrive();
        } else {
          // Saved before postcodes were checked against the list (4 Nov 2026): check it once.
          this.render();
          this.knownState(saved.pc).then((st) => {
            if (!st) {
              try { localStorage.removeItem(SAVED_KEY); } catch (e) { /* private mode */ }
              Object.assign(this.state, { pc: '', st: '', suburb: '', mode: 'ask' });
              this.render();
              return;
            }
            this.state.st = st;
            this.save();
            arrive();
          });
        }
      } else {
        this.render();
      }
    }

    /**
     * The postcode's state if our postcode list has it, or false. Australia Post prices every
     * number in its zone table, 9998 and 9999 included, so the ranges alone let made-up postcodes
     * through. If the list can't be loaded, fall back to the ranges rather than block the quote.
     */
    async knownState(pc) {
      try {
        const row = (await loadJson(this.cfg.postcodes[parseInt(pc[0], 10)]))[pc];
        return row ? row[0] : false;
      } catch (e) {
        return stateOf(pc);
      }
    }

    /**
     * Runs fn once the page is in front of someone: not while the browser prerenders it on a
     * hover, or while it sits in a background tab. Until then the saved place shows with a
     * button.
     */
    whenViewed(fn) {
      const viewed = () => document.visibilityState === 'visible' && !document.prerendering;
      if (viewed()) { fn(); return; }
      this.render();
      const go = () => {
        if (!viewed()) return;
        document.removeEventListener('visibilitychange', go);
        document.removeEventListener('prerenderingchange', go);
        fn();
      };
      document.addEventListener('visibilitychange', go);
      document.addEventListener('prerenderingchange', go);
    }

    get qty() {
      const q = parseInt(this.qtyInput ? this.qtyInput.value : '1', 10);
      return q > 0 ? q : 1;
    }

    handleVariantChange(evt) {
      const variant = evt.detail && evt.detail.variant;
      if (!variant || !variant.id || variant.id === this.variantId) return;
      this.variantId = variant.id;
      this.variantAvailable = variant.available !== false;
      this.requote(0);
    }

    handleClick(evt) {
      const btn = evt.target.closest('[data-sq-act]');
      if (!btn || btn.disabled) return;
      const s = this.state;
      const act = btn.dataset.sqAct;
      if (act === 'change') {
        this.cancelQuote();
        s.mode = 'edit';
        s.err = '';
        s.draft = null;
        this.render();
        // With a mouse the cursor goes in the box. On a phone, focusing it from code reopens the
        // keyboard and can zoom the page (Android even at 16px), so the shopper taps the box.
        const input = this.querySelector('.cz-sq__input');
        if (input && window.matchMedia('(pointer: fine)').matches) { input.focus(); input.select(); }
      } else if (act === 'cancel' || act === 'show') {
        s.mode = 'result';
        s.err = '';
        s.draft = null;
        this.requote(0);
      } else if (act === 'locate') {
        this.locate();
      }
    }

    handleSubmit(evt) {
      evt.preventDefault();
      const input = this.querySelector('.cz-sq__input');
      this.setPostcode(input ? input.value : '');
    }

    handleInput(evt) {
      if (!evt.target.matches('.cz-sq__input')) return;
      const digits = evt.target.value.replace(/\D/g, '').slice(0, 4);
      if (digits !== evt.target.value) evt.target.value = digits;
      // Fetch the list for this first digit while the rest is typed.
      if (digits.length) loadJson(this.cfg.postcodes[parseInt(digits[0], 10)]).catch(() => {});
      if (digits.length === 4) this.setPostcode(digits);
    }

    handleChange(evt) {
      if (!evt.target.matches('.cz-sq__select') || !evt.target.value) return;
      this.state.suburb = evt.target.value;
      this.state.mode = 'result';
      this.save();
      this.requote(0);
    }

    async setPostcode(value) {
      const s = this.state;
      const pc = String(value || '').replace(/\D/g, '');
      const fail = (msg) => {
        s.mode = 'edit';
        s.err = msg;
        s.draft = pc; // the box keeps what was typed, not the last good postcode
        // Only the error line changes: rebuilding the box and putting the cursor back made phones
        // reopen the keyboard and zoom in (iPhone below 16px, Android at 16px too, 4 Nov 2026).
        if (!this.showError(msg)) this.render();
      };
      this.cancelQuote();
      if (!stateOf(pc)) {
        fail('Enter a 4-digit Australian postcode.');
        return;
      }
      const check = this.pcCheck = (this.pcCheck || 0) + 1;
      const st = await this.knownState(pc);
      if (check !== this.pcCheck) return; // a newer postcode was typed meanwhile
      if (!st) {
        fail(`We don't recognise postcode ${pc}. Check it and try again.`);
        return;
      }
      Object.assign(s, { suburb: pc === s.pc ? s.suburb : '', pc, st, fromLoc: false, mode: 'result', err: '', draft: null });
      this.save();
      this.requote(0);
    }

    /** Shows an error under the postcode box without rebuilding it; false if the box isn't on screen. */
    showError(msg) {
      const form = this.querySelector('.cz-sq__form');
      const input = this.querySelector('.cz-sq__input');
      if (!form || !input || input.disabled) return false;
      let err = this.querySelector('.cz-sq__err');
      if (!err) {
        err = document.createElement('p');
        err.className = 'cz-sq__err';
        err.id = `${this.idBase}-err`;
        err.setAttribute('role', 'alert');
        form.insertAdjacentElement('afterend', err);
      }
      err.textContent = msg;
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', err.id);
      return true;
    }

    save() {
      const s = this.state;
      // v: 1 marks a postcode already checked against the list.
      write(localStorage, SAVED_KEY, { pc: s.pc, st: s.st, suburb: s.suburb, v: 1 });
    }

    /** Drops a quote still loading, so its answer can't replace what the shopper is now doing. */
    cancelQuote() {
      clearTimeout(this.timer);
      this.seq += 1;
    }

    requote(delay) {
      const s = this.state;
      if (!s.pc || s.mode === 'edit' || s.mode === 'locating') return;
      if (!this.variantAvailable) {
        clearTimeout(this.timer);
        this.seq += 1;
        s.mode = 'unavailable';
        this.render();
        return;
      }
      s.mode = 'loading';
      this.render();
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.quote(), delay);
    }

    async quote() {
      const s = this.state;
      const seq = ++this.seq;
      const qty = this.qty;
      const key = CACHE_KEY + [this.variantId, qty, s.pc, s.suburb].join(':');
      try {
        let cached = read(sessionStorage, key);
        if (!cached || Date.now() - cached.at > CACHE_MS) {
          cached = { at: Date.now(), ...(await this.fetchRates(qty)) };
          write(sessionStorage, key, cached);
        }
        if (seq !== this.seq) return;

        // Freight carriers need the suburb: a postcode-only quote with nothing to deliver it
        // (empty, or warehouse pickup alone in QLD) asks for one.
        if (!s.suburb && !cached.rates.some((r) => !r.pickup)) {
          const suburbs = await this.suburbsFor(s.pc);
          if (seq !== this.seq) return;
          if (suburbs.length === 1) {
            s.suburb = suburbs[0];
            this.save();
            this.quote();
            return;
          }
          if (suburbs.length > 1) {
            Object.assign(s, { mode: 'suburb', suburbs });
            this.render();
            return;
          }
        }
        Object.assign(s, { mode: 'result', rates: cached.rates, shownQty: cached.qty || qty });
      } catch (e) {
        if (seq !== this.seq) return;
        s.mode = e.unavailable ? 'unavailable' : 'error';
      }
      this.render();
    }

    async fetchRates(qty) {
      const s = this.state;
      const address = { countryCode: 'AU', zip: s.pc, provinceCode: s.st || stateOf(s.pc) };
      if (s.suburb) address.city = s.suburb;
      const created = await this.gql(CREATE, {
        input: {
          lines: [{ merchandiseId: `gid://shopify/ProductVariant/${this.variantId}`, quantity: qty }],
          delivery: { addresses: [{ address: { deliveryAddress: address }, selected: true, oneTimeUse: true }] }
        }
      });
      const result = created && created.data && created.data.cartCreate;
      const cart = result && result.cart;
      // Shopify refuses a line it can't sell (a sold-out size): that is not an outage.
      if (!cart && result && result.userErrors && result.userErrors.length) throw Object.assign(new Error('Not for sale'), { unavailable: true });
      if (!cart) throw new Error('No quote cart');
      // A quantity above stock comes back capped; say how many were priced.
      const line = cart.lines && cart.lines.nodes && cart.lines.nodes[0];

      const parts = await this.gql(RATES, { id: cart.id }, true);
      const rates = [];
      parts.forEach((part) => {
        if (part.errors) throw new Error('Rates query failed');
        (part.incremental || [part]).forEach((inc) => {
          const data = inc.data || {};
          const groups = (data.cart || data).deliveryGroups;
          if (!groups) return;
          groups.nodes.forEach((node) => node.deliveryOptions.forEach((option) => {
            const name = rateName(option);
            const price = parseFloat(option.estimatedCost && option.estimatedCost.amount) || 0;
            if (!rates.some((r) => r.name === name && r.price === price)) {
              rates.push({ name, price, freight: /LARGE FREIGHT/i.test(option.title || ''), pickup: /^WAREHOUSE PICKUP/i.test(option.title || '') });
            }
          }));
        });
      });
      return { rates, qty: line ? line.quantity : qty };
    }

    /** Storefront API call; the rates query is answered as multipart/mixed because of @defer. */
    async gql(query, variables, deferred) {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
      try {
        const res = await fetch(this.cfg.api, {
          method: 'POST',
          signal: ctrl.signal,
          headers: { 'Content-Type': 'application/json', Accept: deferred ? 'multipart/mixed' : 'application/json' },
          body: JSON.stringify({ query, variables })
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const type = res.headers.get('Content-Type') || '';
        const text = await res.text();
        if (!deferred) return JSON.parse(text);
        if (!type.includes('multipart')) return [JSON.parse(text)];
        const boundary = (type.match(/boundary="?([^";]+)"?/) || [])[1];
        return text.split(`--${boundary}`)
          .filter((chunk) => chunk.includes('{'))
          .map((chunk) => JSON.parse(chunk.slice(chunk.indexOf('{'), chunk.lastIndexOf('}') + 1)));
      } finally {
        clearTimeout(timer);
      }
    }

    async suburbsFor(pc) {
      const data = await loadJson(this.cfg.postcodes[parseInt(pc[0], 10)]);
      const row = data[pc];
      if (!row) return [];
      if (row[0]) this.state.st = row[0];
      const names = [];
      for (let i = 1; i < row.length; i += 3) names.push(row[i]);
      return names;
    }

    locate() {
      const s = this.state;
      this.cancelQuote();
      s.mode = 'locating';
      s.err = '';
      this.render();
      const fail = (msg) => { s.mode = 'edit'; s.err = msg; this.render(); };
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          const found = await this.nearest(pos.coords.latitude * 100, pos.coords.longitude * 100);
          if (!found) { fail('Your location isn\'t near an Australian postcode. Enter your postcode instead.'); return; }
          Object.assign(s, found, { fromLoc: true, mode: 'result' });
          this.save();
          this.requote(0);
        } catch (e) {
          fail('We couldn\'t look up your location. Enter your postcode instead.');
        }
      }, () => fail('We couldn\'t get your location. Enter your postcode instead.'), { timeout: 10000, maximumAge: 600000 });
    }

    /** Nearest suburb to a point (lat and lng x100, as in the data files). */
    async nearest(lat, lng) {
      const centres = await loadJson(this.cfg.centres);
      const kx = Math.cos((lat / 100) * Math.PI / 180);
      const dist = (a, b) => (a - lat) ** 2 + ((b - lng) * kx) ** 2;
      const near = Object.keys(centres)
        .map((pc) => [pc, dist(centres[pc][0], centres[pc][1])])
        .sort((a, b) => a[1] - b[1])
        .slice(0, 4);
      if (!near.length || near[0][1] > 150 * 150) return null; // over ~150 km from any postcode
      let best = null;
      for (const [pc] of near) {
        const row = (await loadJson(this.cfg.postcodes[parseInt(pc[0], 10)]))[pc];
        if (!row) continue;
        for (let i = 1; i < row.length; i += 3) {
          const d = dist(row[i + 1], row[i + 2]);
          if (!best || d < best.d) best = { d, pc, st: row[0], suburb: row[i] };
        }
      }
      if (!best) return { pc: near[0][0], st: stateOf(near[0][0]), suburb: '' };
      return { pc: best.pc, st: best.st, suburb: best.suburb };
    }

    formHtml() {
      const s = this.state;
      const busy = s.mode === 'locating';
      const dis = busy ? ' disabled' : '';
      return `<form class="cz-sq__form" novalidate>
          <div class="cz-sq__field"><input class="cz-sq__input" id="${this.idBase}-pc" size="4" inputmode="numeric" autocomplete="postal-code" maxlength="4" placeholder="Postcode" aria-label="Postcode" value="${busy ? '' : esc(s.draft != null ? s.draft : s.pc)}"${s.err ? ` aria-invalid="true" aria-describedby="${this.idBase}-err"` : ''}${dis}></div>
          <button type="submit" class="cz-sq__go"${dis}>Check</button>
        </form>
        ${s.err ? `<p class="cz-sq__err" id="${this.idBase}-err" role="alert">${esc(s.err)}</p>` : ''}
        ${this.canLocate ? `<button type="button" class="cz-sq__loc" data-sq-act="locate"${dis}>${ICON.target}${busy ? 'Finding your location…' : 'Use my location'}</button>` : ''}`;
    }

    render() {
      const s = this.state;
      const head = (text, action) => `<div class="cz-sq__head">${ICON.truck}<span>${text}</span>${action || ''}</div>`;
      const place = `<b>${s.suburb ? `${esc(s.suburb)} ` : ''}${esc(s.st || stateOf(s.pc) || '')} ${esc(s.pc)}</b>${s.fromLoc ? ' <small>(your location)</small>' : ''}`;
      const change = '<button type="button" class="cz-sq__link" data-sq-act="change">Change</button>';
      let html;

      if (s.mode === 'ask' || s.mode === 'edit' || s.mode === 'locating') {
        const cancel = s.pc ? '<button type="button" class="cz-sq__link" data-sq-act="cancel">Cancel</button>' : '';
        html = head('See what delivery costs', cancel) + this.formHtml();
      } else if (s.mode === 'ready') {
        html = `${head(`Delivery to ${place}`, change)}
          <button type="button" class="cz-sq__show" data-sq-act="show">Show delivery prices</button>`;
      } else if (s.mode === 'loading') {
        html = `${head(`Delivery to ${place}`, change)}
          <div class="cz-sq__skeleton" aria-hidden="true"><i></i><i></i><i></i></div>
          <p class="cz-sq__foot">Checking prices for ${items(this.qty)}…</p>`;
      } else if (s.mode === 'suburb') {
        const id = `${this.idBase}-suburb`;
        html = `${head(`Delivery to ${place}`, change)}
          <label class="cz-sq__label" for="${id}">Choose your suburb to see freight prices</label>
          <div class="cz-sq__field cz-sq__field--select"><select class="cz-sq__select" id="${id}">
            <option value="">Suburb</option>${s.suburbs.map((n) => `<option>${esc(n)}</option>`).join('')}
          </select></div>`;
      } else if (s.mode === 'unavailable') {
        html = `${head(`Delivery to ${place}`, change)}<p class="cz-sq__msg">This option is sold out. Choose another to see delivery prices.</p>`;
      } else if (s.mode === 'error') {
        html = `${head(`Delivery to ${place}`, change)}<p class="cz-sq__msg">Delivery prices aren't available right now. You'll see them at checkout.</p>`;
      } else if (!s.rates.length) {
        html = `${head(`Delivery to ${place}`, change)}<p class="cz-sq__msg">We can't quote delivery here online. <a href="/pages/contact">Contact us</a> and we'll sort it out.</p>`;
      } else {
        const rows = s.rates.map((r) => `<li class="cz-sq__rate"><span>${esc(r.name)}</span><span class="cz-sq__price${r.price === 0 ? ' cz-sq__price--free' : ''}">${money(r.price)}</span></li>`).join('');
        const freight = s.rates.some((r) => r.freight);
        html = `${head(`Delivery to ${place}`, change)}
          <ul class="cz-sq__rates" role="list">${rows}</ul>
          <p class="cz-sq__foot">For ${items(s.shownQty || this.qty)}${s.shownQty && s.shownQty < this.qty ? ', all we have in stock' : ''}. ${freight ? 'Freight is confirmed at checkout with your full address.' : 'Checkout confirms the price.'}</p>`;
      }
      this.body.innerHTML = html;
      this.body.setAttribute('aria-busy', String(s.mode === 'loading'));
    }
  }

  customElements.define('shipping-quote', ShippingQuote);
}
